import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import type { PrequalificationCandidateDto } from "@/api/buyers";
import {
  Button,
  EmptyState,
  Input,
  Skeleton,
  StatusBadge,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { sizedClause } from "./prequalification/qualifiedAmount";
import {
  useLinkListingSellerPrequalification,
  useLinkSellerPrequalification,
  useListingSellerPrequalificationCandidates,
  useSellerPrequalificationCandidates,
} from "@/hooks/buyers/useSellerPrequalification";

/**
 * Pick the prequalification this listing's seller completed.
 *
 * 🔴 **Suggestions, confirmed one at a time — never an automatic link.** The
 * same rule the buyer picker enforces, for the same reason and with one extra
 * edge: a seller prequalification carries the financial position of a person
 * Phoenix has usually never spoken to directly, introduced by a broker, and the
 * link decides which business that position is attributed to.
 *
 * 🔴 **A prequalification another LISTING already claims is called out loudly.**
 * It is not refused — one seller genuinely can be marketing two businesses, and
 * a unique index would block the honest case to catch the mistaken one — but it
 * is the specific mistake a confirm step exists to prevent, so it is shown as an
 * alert rather than a footnote.
 *
 * **The search box has no default.** The buyer picker seeds itself from the
 * buyer's own email; this module holds no seller contact record at all, so an
 * empty box returns nothing rather than the whole book.
 *
 * 🔴 **Addressed by EITHER the listing or a submission against it, and both
 * arms are live.** The listing is the record the link lands on and the primary
 * address; the submission address stays because it carries a different — and
 * stricter — authorization gate, so resolving one into the other would widen who
 * may write. Exactly one id is supplied; the unused arm's hooks are disabled by
 * their own empty-id guard, which is why they can be called unconditionally
 * without breaking the rules of hooks.
 */
export function SellerPrequalificationCandidateModal({
  submissionId,
  listingId,
  open,
  onClose,
}: {
  submissionId?: string;
  listingId?: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const [search, setSearch] = useState("");

  // Both called, one enabled. `useQuery`'s `enabled` already gates on a truthy
  // id in each hook, and a `useMutation` performs nothing until it is fired, so
  // the disabled arm costs a hook slot and no request.
  const bySubmission = useSellerPrequalificationCandidates(submissionId ?? "", search, open);
  const byListing = useListingSellerPrequalificationCandidates(listingId ?? "", search, open);
  const linkBySubmission = useLinkSellerPrequalification(submissionId ?? "");
  const linkByListing = useLinkListingSellerPrequalification(listingId ?? "");

  const candidates = listingId ? byListing : bySubmission;
  const link = listingId ? linkByListing : linkBySubmission;

  const rows = candidates.data ?? [];
  const searching = search.trim().length > 0;

  function onLink(prequalSubmissionId: string) {
    link.mutate(prequalSubmissionId, {
      onSuccess: () => {
        toast.success("Seller prequalification linked.");
        onClose();
      },
      onError: (err) =>
        toast.error((err as { message?: string })?.message ?? "Could not link it."),
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Link a seller prequalification"
      size="lg"
    >
      <div className="grid gap-space-4">
        <div>
          <label className="block">
            <span className="text-caption uppercase tracking-wide text-gray-mid">
              Search by email
            </span>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="The seller's email address"
              data-testid="seller-prequal-candidate-search"
              aria-label="Search seller prequalifications by email"
              className="mt-space-1"
            />
          </label>
          <p className="text-caption text-gray-mid mt-space-1 mb-0">
            Matches are suggestions, not proof of identity. Check the name and the
            date before linking — this attaches to the listing, so every buyer
            submitted against this business will carry it.
          </p>
        </div>

        {searching && candidates.isLoading && <Skeleton className="h-40 w-full" />}

        {!searching && (
          <EmptyState
            title="Search for the seller"
            description="Type the seller's email address to find a prequalification they already completed."
          />
        )}

        {searching && !candidates.isLoading && rows.length === 0 && (
          <EmptyState
            title="No seller prequalifications found"
            description="No seller submission matches that address. Send the seller an invitation instead."
          />
        )}

        <div className="grid gap-space-3">
          {rows.map((c) => (
            <SellerCandidateRow
              key={c.submissionId}
              candidate={c}
              disabled={link.isPending}
              onLink={() => onLink(c.submissionId)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}

function SellerCandidateRow({
  candidate: c,
  disabled,
  onLink,
}: {
  candidate: PrequalificationCandidateDto;
  disabled: boolean;
  onLink: () => void;
}) {
  const claimed = Boolean(c.alreadyLinkedToListingId);

  return (
    <div
      className="border border-border-light rounded-md p-space-3 bg-white-pure"
      data-testid="seller-prequal-candidate-row"
    >
      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="min-w-0">
          <div className="text-small font-medium text-onyx">{c.fullName}</div>
          <div className="text-caption text-gray-mid">
            {c.email}
            {c.referenceNumber ? ` · ${c.referenceNumber}` : ""}
          </div>
          <div className="text-caption text-gray-mid mt-space-1">
            Submitted <Timestamp value={c.submittedAtUtc} />
            {sizedClause(c)}
          </div>
        </div>

        <div className="flex flex-col items-end gap-space-2 shrink-0">
          {/* No letter badge here. A seller prequalification produces no
              letter — the buyer's is what a submission package embeds, and
              there is no seller equivalent — so showing "No letter" would
              read as something missing rather than something that does not
              exist. */}
          <StatusBadge tone={c.outcome === "Pass" ? "ok" : "neutral"}>
            {c.outcome}
          </StatusBadge>
          <Button
            variant="secondary"
            onClick={onLink}
            disabled={disabled}
            data-testid="seller-prequal-candidate-link"
          >
            Link this one
          </Button>
        </div>
      </div>

      {claimed && (
        <p
          className="flex items-start gap-space-1 text-small text-status-warning mt-space-3 mb-0"
          role="alert"
          data-testid="seller-prequal-candidate-claimed"
        >
          <AlertTriangle size={14} aria-hidden className="mt-[2px] shrink-0" />
          <span>
            Already linked to {c.alreadyLinkedToListingName}. One seller can be
            marketing two businesses, but check this is the same person and the
            right listing first.
          </span>
        </p>
      )}
    </div>
  );
}
